import { uid } from '../../../../lib/uid';
import type { LearningDevelopmentCatalogSlug } from '../learningDevelopmentTypes';

/*
  Plausible vocabulary per catalog, which is the right call here for the reason
  `sampleCompetency`'s wordings are: none of these values ever leaves `rsys_` or lands in the
  identity store, and an unreadable one would make the generated form worse at the one job it
  has — showing what the field is for. The rule is about where a value LANDS, not which
  generator it came from.

  The SPONSORS are the one set to watch: they are institution names, so every one is prefixed
  "Test" and none names a real academy. An invented-but-realistic institution in a list an
  employer reads as its own vocabulary is a row somebody would eventually believe.
*/
const NAMES: Record<LearningDevelopmentCatalogSlug, readonly string[]> = {
  types: ['Coaching Engagement', 'Scholarship Grant', 'Study Tour', 'Secondment'],
  categories: ['Ethics and Integrity', 'Digital Literacy', 'Client Service', 'Safety'],
  interventions: ['Webinar', 'Blended Learning', 'Job Shadowing', 'Structured Reading'],
  sponsors: [
    'Test Civil Service Institute',
    'Test Development Academy',
    'Test Regional Training Center',
    'Test Institute of Public Administration',
  ],
};

const DESCRIPTIONS: Record<LearningDevelopmentCatalogSlug, string> = {
  types: 'Sample L&D type. Delete it once the real vocabulary is in.',
  categories: 'Sample L&D category. Delete it once the real vocabulary is in.',
  interventions: 'Sample development intervention. Delete it once the real vocabulary is in.',
  sponsors: 'Sample sponsor. Delete it once the real list is in.',
};

/**
 * A sample vocabulary row, so the form fills in one click.
 *
 * **The name is suffixed because it is unique among active rows.** Autogenerate is most useful
 * clicked repeatedly, and the create is idempotent on the name — so without a suffix the second
 * click would quietly return the first row rather than adding one, which reads as the button
 * having stopped working.
 *
 * Applied on an edit as well as a create, per the Autogenerate rule: these are development
 * records, and the form a reader most often needs filled fast is the one over a half-empty row.
 */
export function sampleLearningDevelopmentEntry(catalog: LearningDevelopmentCatalogSlug): {
  name: string;
  description: string;
} {
  const tag = uid().slice(0, 4).toUpperCase();
  const pool = NAMES[catalog];

  return {
    name: `${pool[Math.floor(Math.random() * pool.length)]} ${tag}`,
    description: `${DESCRIPTIONS[catalog].replace('Sample', `Sample ${tag}`)}`,
  };
}
